import { Box, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Checkbox } from 'src/components/atoms/Checkbox';
import type { ModelType } from 'src/api/models';
import { MODEL_TYPE_OPTIONS } from 'src/utils/modelTypes';

type ModelTypeRadioProps = {
  value: ModelType | undefined;
  onChange: (next: ModelType) => void;
  isDisabled?: boolean;
  /** Per-option disabling: value is the reason shown as a tooltip. */
  disabledOptions?: Partial<Record<ModelType, string>>;
  /** Options not rendered at all (e.g. onboarding hides 'lt' — a long-term
   * model needs an existing published MM model, impossible during signup). */
  hiddenOptions?: ModelType[];
};

export const ModelTypeRadio: React.FC<ModelTypeRadioProps> = ({
  value,
  onChange,
  isDisabled,
  disabledOptions,
  hiddenOptions,
}) => {
  return (
    <Box
      border="1px solid"
      borderColor="surface.border"
      borderRadius="24px"
      p={6}
      bg="surface.panel">
      <Text fontSize="16px" fontWeight={600} color="content.title" mb={6}>
        Model type
      </Text>

      <VStack align="stretch" spacing={5}>
        {MODEL_TYPE_OPTIONS.filter(
          (opt) => !hiddenOptions?.includes(opt.value),
        ).map((opt) => {
          const disabledReason = disabledOptions?.[opt.value];
          return (
            <Box key={opt.value} opacity={disabledReason ? 0.5 : 1}>
              <Tooltip
                label={disabledReason}
                isDisabled={!disabledReason}
                placement="top-start"
                hasArrow>
                <Box display="inline-block">
                  <Checkbox
                    isChecked={value === opt.value}
                    isDisabled={isDisabled || Boolean(disabledReason)}
                    onChange={() => onChange(opt.value)}
                    label={
                      <Text
                        fontSize="16px"
                        fontWeight={500}
                        color="content.title">
                        {opt.label}
                      </Text>
                    }
                  />
                </Box>
              </Tooltip>
              <Text
                fontSize="14px"
                color="content.muted"
                ml={6}
                mt={1}
                lineHeight="1.5">
                {opt.description}
              </Text>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};
